<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_ACCOUNT_CATEGORY')"
            label="Add"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          sm="12"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />

            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="accountCategoryRef"
      :items="customFieldList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_ACCOUNT_CATEGORY')"
              @click="onSelectCustomFieldItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Modify</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectCustomFieldItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_CUSTOM_FIELD' : 'ACTIVATE_CUSTOM_FIELD')"
              @click="customFieldStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? 'Deactivate' : 'Activate' }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      no-close-on-backdrop
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="customFieldRules">
        <b-form>
          <b-row class="match-height">
            <!-- Context -->
            <b-col md="6">
              <SelectField
                id="context"
                v-model="customFieldFormData.context"
                label="Context"
                :options="customContextOptions"
                error-name="Context"
                rules="required"
                :reduce="option => option.value"
                item="label"
                placeholder="Select"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Context -->

            <!-- Name -->
            <b-col md="6">
              <InputField
                id="name"
                v-model="customFieldFormData.name"
                label="Name"
                placeholder="Enter name"
                type="text"
                error-name="Name"
                rules="required"
                for="name"
                :readonly="isEditable"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Name -->
            <!-- Label -->
            <b-col md="6">
              <InputField
                id="label"
                v-model="customFieldFormData.label"
                label="Label"
                placeholder="Enter label"
                type="text"
                error-name="Label"
                rules="required"
                for="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Label -->

            <!-- Data Type -->
            <b-col md="6">
              <SelectField
                id="data-type"
                v-model="customFieldFormData.dataType"
                label="Data Type"
                :options="customDataTypeOptions"
                error-name="Data Type"
                rules="required"
                :reduce="option => option.value"
                item="label"
                placeholder="Select"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Data Type -->

            <!-- Field Type -->
            <b-col md="6">
              <SelectField
                id="field-type"
                v-model="customFieldFormData.fieldType"
                label="Field Type"
                :options="customFieldTypeOptions"
                error-name="Field Type"
                rules="required"
                :reduce="option => option.value"
                item="label"
                placeholder="Select"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Field Type -->

          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      title="Filters"
      modal-footer
      @ok="fetchCustomFields"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Context -->
        <b-col md="6">
          <SelectField
            id="context"
            v-model="filter.context"
            label="Context"
            placeholder="Select context"
            :reduce="option => option"
            :options="filterContextOptions"
            item="label"
          />
        </b-col>
        <!-- Context -->
        <!-- Name  -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            label="Name"
            placeholder="Enter name"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            label="Status"
            placeholder="Select Status"
            :reduce="option => option"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge,
  BDropdown, BModal, BForm, BSpinner, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import { toDateWithYear } from '../../../helpers/formatDate'
import Button from '../../components/button/Button.vue'
import { operationPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BSpinner,
    BDropdownItem,
    BModal,
    BBadge,
    BForm,
    InputField,
    SelectField,
    ValidationObserver,
    TablePagination,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'name',
      isReadonly: false,
      buttonLabel: 'Save',
      modalTitle: 'Create Custom Fields',
      isBusy: false,
      required,
      searchURL: '',
      sortDesc: false,
      isEditable: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'context', sortable: true },
        { key: 'name', sortable: true },
        { key: 'label', sortable: true },
        { key: 'dataType', sortable: true },
        { key: 'fieldType', sortable: true },
        { key: 'status', sortable: true },
        { key: 'actions' },
      ],
      customFieldFormData: {
        context: '',
        name: '',
        label: '',
        dataType: '',
        fieldType: '',
      },
      filter: {
        context: '',
        name: '',
        status: '',
      },
      customContextOptions: [
        { value: 1, label: 'CustomerInformation' },
        { value: 2, label: 'CustomerAccount' },
        { value: 3, label: 'LoanAccount' },
        { value: 4, label: 'FixedDepositAccount' },
        { value: 5, label: 'Branch' },
        { value: 6, label: 'User' },
      ],
      customDataTypeOptions: [
        { value: 1, label: 'String' },
        { value: 2, label: 'Boolean' },
        { value: 3, label: 'Number' },
        { value: 4, label: 'DateTime' },
      ],
      customFieldTypeOptions: [
        { value: 1, label: 'Text' },
        { value: 2, label: 'Number' },
        { value: 3, label: 'Slider' },
        { value: 4, label: 'Calendar' },
      ],
      filterContextOptions: [
        'CustomerInformation',
        'CustomerAccount',
        'LoanAccount',
        'FixedDepositAccount',
        'Branch',
        'User',
      ],
      statusOptions: [
        'Active',
        'InActive',
      ],
      customFieldList: [],
    }
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCustomFields()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCustomFields()
    },
  },
  mounted() {
    this.fetchCustomFields()
  },
  methods: {
    ...mapActions({
      getCustomFields: 'operations/getcustomFieldList',
      updateCustomField: 'operations/updateCustomFieldAction',
      createCustomField: 'operations/createCustomFieldAction',
      updateCustomFieldStatus: 'operations/updateCustomFieldStatus',
    }),

    handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.customFieldRules.validate().then(async success => {
          if (success) {
            this.confirm(this.customFieldAction, { text: `Are you sure you want to ${this.buttonLabel} this Custom Field?` })
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.hideModal('modal-lg')
      }
    },
    async customFieldAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save'
          ? await this.createCustomField(this.customFieldFormData)
          : await this.updateCustomField(this.customFieldFormData)

        const { status } = response
        if (status === 200) {
          this.fetchCustomFields()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: `Your request to ${this.buttonLabel} Custom Field was successful` })
        }
      } catch (error) {
        error.response.data.errors.forEach(err => {
          this.alertError({ text: `Your request to ${this.buttonLabel} Custom Field failed. Reason: ${err.errorMessage}` })
        })
      } finally {
        this.isLoading = false
      }
    },

    customFieldStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? 'Deactivate' : 'Activate'
      const permission = items.status === 'Active' ? operationPermissions.DEACTIVATE_CUSTOM_FIELD : operationPermissions.ACTIVATE_CUSTOM_FIELD

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateCustomFieldStatus({
            statusValues, actionType, permission,
          })
          if (response.status === 200) {
            this.fetchCustomFields()
            this.alertSuccess({ text: `Your request to ${action} Custom Field was successful` })
            this.showSpinner = false
          }
        } catch (error) {
          this.alertError({ text: `Your request to ${action} Custom Field failed. Reason: ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(updateStatus, { title: '', text: `Are you sure you want to ${action} this Custom Field?` })
    },

    async onSelectCustomFieldItem(items, activity, val) {
      try {
        this.customFieldFormData = { ...items }
        this.isReadonly = val !== 'edit'
        this.isEditable = true
        this.modalTitle = val === 'edit' ? 'Edit Custom Fields' : 'Custom Fields'
        this.buttonLabel = 'Update'
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchCustomFields() {
      this.isBusy = true
      this.hideModal('modal-filter')
      try {
        const response = await this.getCustomFields({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.customFieldList = data.data
          this.totalCount = data.totalItems
        } else {
          this.customFieldList = []
          this.totalCount = 0
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const { errorMessage } = responseError
          this.alertError({ text: errorMessage })
        })
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCustomFields()
    },
    resetModal() {
      this.customFieldFormData = { ...null }
      this.isReadonly = false
      this.isEditable = false
      this.isLoading = false
      this.modalTitle = 'Create Custom Fields'
      this.buttonLabel = 'Save'
    },
  },
}
</script>
